/**
 * Dev Mode Type Utilities
 * 
 * Development-only feature to switch between two testing modes:
 * - "web-mobile": Bypass auth, auto-load Dev_Workspace (for mobile testing)
 * - "web-only": Full authentication required (for testing permissions with multiple accounts)
 * 
 * Only available when REACT_APP_BYPASS_AUTH=true
 */

export type DevModeType = "web-mobile" | "web-only";

const DEV_MODE_TYPE_KEY = "devModeType";

/**
 * Check if dev mode is enabled (REACT_APP_BYPASS_AUTH=true)
 */
export const isDevModeEnabled = (): boolean => {
  if (process.env.NODE_ENV === "production") {
    return false;
  }
  return process.env.REACT_APP_BYPASS_AUTH === "true";
};

/**
 * Get current dev mode type
 * Defaults to "web-mobile" if not set
 */
export const getDevModeType = (): DevModeType => {
  if (!isDevModeEnabled()) {
    return "web-mobile"; // Default, but won't be used in production
  }

  const stored = localStorage.getItem(DEV_MODE_TYPE_KEY);
  if (stored === "web-only" || stored === "web-mobile") {
    return stored as DevModeType;
  }

  // Default to web-mobile for backward compatibility
  return "web-mobile";
};

/**
 * Set dev mode type
 */
export const setDevModeType = (type: DevModeType): void => {
  if (!isDevModeEnabled()) {
    console.warn("Dev mode is not enabled. Cannot set dev mode type.");
    return;
  }

  localStorage.setItem(DEV_MODE_TYPE_KEY, type);
  console.log(`[Dev Mode] Switched to: ${type}`);
};

/**
 * Check if bypass auth should be active
 * Returns true only if dev mode is enabled AND type is "web-mobile"
 */
export const shouldUseBypassAuth = (): boolean => {
  if (!isDevModeEnabled()) {
    return false;
  }

  const modeType = getDevModeType();
  return modeType === "web-mobile";
};
